/**
 * Personalkosten: Pauschalen.
 * Aus pages/ProjektDetailPage.tsx herausgelöst (Plan Projekt-Tracking, Etappe 2) —
 * verschoben, nicht umgeschrieben.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fehlerText } from "@vimaana/ui";
import { useProjektStunden, useProjektPauschalen, useAddPauschale, useDeletePauschale, useFreigebenPauschale, usePatchPauschale, useTeamMembers } from "../../hooks/usePlanung";
import type { Pauschale } from "../../lib/types";
import { formatEur } from "../../lib/planer";
import { zahl } from "./seiteHilfen";

/* ══ Personalkosten: Pauschalen ══════════════════════════════════════════ */

export function PauschalenBlock({ projektId }: { projektId: string }) {
  const { data: members = [] } = useTeamMembers();
  const { data: pauschalen = [] } = useProjektPauschalen(projektId);
  const { data: zeilen = [] } = useProjektStunden(projektId);
  const add = useAddPauschale();
  const del = useDeletePauschale();
  const freigabe = useFreigebenPauschale();
  const patch = usePatchPauschale();
  // Nachträglich anpassen (12.10.2026): statt löschen und neu anlegen — dabei
  // verloren die Einsätze ihre Verknüpfung (Fall Marcel Schneider).
  const [bearbeiten, setBearbeiten] = useState<{ id: string; bez: string; betrag: string; datum: string } | null>(null);
  const fehler = del.error || patch.error;
  const [helferId, setHelferId] = useState("");
  const [bez, setBez] = useState("");
  const [betrag, setBetrag] = useState("");
  const [datum, setDatum] = useState("");

  const betragNum = zahl(betrag);
  const kannAdd = !!helferId && betragNum != null && betragNum >= 0 && !add.isPending;

  /**
   * Wer im Projekt arbeitet, steht oben — der Rest des Teams darunter.
   *
   * Eine Pauschale ohne Einsatz ist zulässig (jemand arbeitet auf Festpreis,
   * ohne dass Stunden erfasst werden), deshalb sperren wir die Übrigen nicht
   * aus, sondern schieben sie nur nach hinten.
   */
  const { imProjekt, weitere } = useMemo(() => {
    const beteiligt = new Set(
      zeilen.filter((z) => z.person_typ !== "inhaber").map((z) => z.person_id),
    );
    const helfer = members.filter((m) => m.active !== false && m.role !== "inhaber");
    return {
      imProjekt: helfer.filter((m) => beteiligt.has(m.id)),
      weitere: helfer.filter((m) => !beteiligt.has(m.id)),
    };
  }, [members, zeilen]);

  const gruppen = useMemo(() => {
    const m = new Map<string, { name: string; items: Pauschale[] }>();
    for (const p of pauschalen) {
      const g = m.get(p.helfer_id) ?? { name: p.helfer_name || p.helfer_id, items: [] as Pauschale[] };
      g.items.push(p);
      m.set(p.helfer_id, g);
    }
    return [...m.values()];
  }, [pauschalen]);

  return (
    <div className="vg-unterblock">
      <div className="vg-detail-section-title">Pauschalen</div>
      <p className="vg-detail-section-hint">
        Pauschal bezahlte Teilabschnitte je Helfer (netto) — zusätzlich zu erfassten Stunden,
        zählen als Personalkosten. Ins Lohnkonto des Helfers gehen sie erst, wenn du sie
        <b> freigibst</b>.
      </p>
      {gruppen.length === 0 ? (
        <div className="vg-detail-section-hint">Noch keine Pauschalen.</div>
      ) : (
        gruppen.map((g) => (
          <div key={g.name} className="vg-pauschale-gruppe">
            <div className="vg-pauschale-gruppe-name">{g.name}</div>
            {g.items.map((p) => {
              const frei = !!p.freigegeben_am;
              const offen = p.slot_offen ?? 0;
              return (
              <div key={p.id} className="vg-pauschale-eintrag">
                {bearbeiten?.id === p.id ? (
                  <div className="vg-pauschale-add">
                    <input value={bearbeiten.bez} onChange={(e) => setBearbeiten({ ...bearbeiten, bez: e.target.value })}
                      placeholder="Bezeichnung" aria-label="Bezeichnung" style={{ flex: 1, minWidth: 120 }} />
                    <input value={bearbeiten.betrag} onChange={(e) => setBearbeiten({ ...bearbeiten, betrag: e.target.value })}
                      placeholder="Betrag €" aria-label="Betrag" inputMode="decimal" style={{ width: 96 }}
                      disabled={!!p.beleg_id} title={p.beleg_id ? "Mit einer Rechnung abgerechnet — Betrag in der Belegverarbeitung lösen" : undefined} />
                    <input type="date" value={bearbeiten.datum} onChange={(e) => setBearbeiten({ ...bearbeiten, datum: e.target.value })}
                      aria-label="Datum" style={{ width: 150 }} />
                    <button type="button" className="vg-btn vg-btn-primary"
                      disabled={patch.isPending || zahl(bearbeiten.betrag) == null || (zahl(bearbeiten.betrag) as number) < 0}
                      onClick={() => patch.mutate(
                        { projektId, id: p.id, body: {
                          bezeichnung: bearbeiten.bez.trim(), datum: bearbeiten.datum || null,
                          ...(p.beleg_id ? {} : { betrag: zahl(bearbeiten.betrag) as number }) } },
                        { onSuccess: () => setBearbeiten(null) },
                      )}>
                      Speichern
                    </button>
                    <button type="button" className="vg-btn" onClick={() => setBearbeiten(null)}>Abbrechen</button>
                  </div>
                ) : (
                <div className="vg-pauschale-zeile">
                  <span>{p.bezeichnung || "Pauschale"}{p.datum ? ` · ${p.datum}` : ""}</span>
                  <span className="vg-pauschale-betrag">{formatEur(p.betrag, { round: true })}</span>
                  {/* Mit einer Rechnung abgerechnet: Löschen nähme der Rechnung ihren
                      Nachweis — gelöst wird in der Belegverarbeitung. */}
                  <button
                    type="button"
                    className="vg-btn"
                    onClick={() => { patch.reset(); setBearbeiten({ id: p.id, bez: p.bezeichnung || "", betrag: String(p.betrag).replace(".", ","), datum: p.datum || "" }); }}
                  >
                    Ändern
                  </button>
                  <button
                    type="button"
                    className="vg-pauschale-del"
                    title={p.beleg_id ? "Mit einer Rechnung abgerechnet — in der Belegverarbeitung lösen" : "Löschen"}
                    disabled={!!p.beleg_id}
                    onClick={() => del.mutate({ projektId, id: p.id })}
                  >
                    ✕
                  </button>
                </div>
                )}
                {p.notiz && <div className="vg-pauschale-termine" style={{ whiteSpace: "pre-line" }}>{p.notiz}</div>}
                {/* Die Freigabe steht an der Position selbst — sie ist die
                    Entscheidung, die hier ansteht. Die offenen Termine
                    daneben, damit niemand freigibt, bevor gearbeitet wurde. */}
                <div className="vg-pauschale-freigabe">
                  <button
                    type="button"
                    className={"vg-freigabe-knopf" + (frei ? " is-frei" : "")}
                    disabled={freigabe.isPending}
                    onClick={() => freigabe.mutate({ projektId, id: p.id, frei: !frei })}
                  >
                    {frei ? "✓ verdient" : "Als verdient freigeben"}
                  </button>
                  {p.beleg_id && (
                    <Link className="vg-pauschale-rechnung" to={`/finanzen?reiter=belege&beleg=${encodeURIComponent(p.beleg_id)}`}>
                      abgerechnet mit Rechnung{p.beleg_datum ? ` vom ${p.beleg_datum.split("-").reverse().join(".")}` : ""}
                    </Link>
                  )}
                  <span className="vg-pauschale-termine">
                    {offen > 0
                      ? `${offen} von ${p.slot_count} Terminen noch offen`
                      : (p.slot_count ? `${p.slot_count} Termine erledigt` : "kein Termin verknüpft")}
                  </span>
                </div>
              </div>
              );
            })}
          </div>
        ))
      )}
      {fehler && <p className="vg-detail-section-hint" role="alert" style={{ color: "var(--vg-red)" }}>{fehlerText(fehler)}</p>}
      <div className="vg-pauschale-add">
        <select value={helferId} onChange={(e) => setHelferId(e.target.value)} style={{ minWidth: 170 }}>
          <option value="">Helfer wählen…</option>
          {imProjekt.length > 0 && (
            <optgroup label="Im Projekt">
              {imProjekt.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </optgroup>
          )}
          {weitere.length > 0 && (
            <optgroup label={imProjekt.length > 0 ? "Weitere Helfer" : "Team"}>
              {weitere.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </optgroup>
          )}
        </select>
        <input value={bez} onChange={(e) => setBez(e.target.value)} placeholder="Bezeichnung" style={{ flex: 1, minWidth: 120 }} />
        <input value={betrag} onChange={(e) => setBetrag(e.target.value)} placeholder="Betrag €" inputMode="decimal" style={{ width: 96 }} />
        <input type="date" value={datum} onChange={(e) => setDatum(e.target.value)} style={{ width: 150 }} />
        <button
          type="button"
          className="vg-btn vg-btn-primary"
          disabled={!kannAdd}
          onClick={() =>
            add.mutate(
              { projektId, body: { helfer_id: helferId, bezeichnung: bez.trim(), betrag: betragNum as number, datum: datum || null } },
              { onSuccess: () => { setBez(""); setBetrag(""); setDatum(""); } },
            )
          }
        >
          + Hinzufügen
        </button>
      </div>
    </div>
  );
}
